(() => {
  const rootPath = window.Shopify?.routes?.root || '/';
  const cartUrl = `${rootPath}cart.js`;
  const cartAddUrl = `${rootPath}cart/add.js`;
  const cartUpdateUrl = `${rootPath}cart/update.js`;
  const giftProperty = '_femi_free_gift';
  const requiredGiftProperty = '_femi_bundle_gift_variant';
  const bundleQuantityProperty = '_femi_bundle_quantity';
  const giftRulesStorageKey = 'femilabs-bundle-gift-rules';
  let cartSyncInProgress = false;

  const getCartRenderer = () => document.querySelector('cart-drawer') || document.querySelector('cart-notification');

  const getSectionsPayload = () => {
    const renderer = getCartRenderer();
    const sections = typeof renderer?.getSectionsToRender === 'function'
      ? renderer.getSectionsToRender().map((section) => section.id)
      : ['cart-drawer', 'cart-icon-bubble'];
    return { renderer, sections };
  };

  const renderCart = (state, renderer) => {
    if (state?.sections && typeof renderer?.renderContents === 'function') {
      renderer.renderContents(state);
      renderer.classList.remove('is-empty');
    }
  };

  const requestJson = async (url, options = {}) => {
    const response = await fetch(url, {
      credentials: 'same-origin',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json', ...(options.headers || {}) },
      ...options
    });
    const state = await response.json();
    if (!response.ok || state.status) {
      throw new Error(state.description || state.message || 'Възникна грешка при обновяване на количката.');
    }
    return state;
  };

  const setGiftPanelState = (panel, quantity) => {
    if (!panel) return;
    const minimum = Number(panel.dataset.minQuantity || 2);
    const unlocked = Number(quantity || 1) >= minimum;
    panel.classList.toggle('is-unlocked', unlocked);
    panel.classList.toggle('is-locked', !unlocked);
    panel.dataset.unlocked = String(unlocked);
  };

  const syncGiftPanels = (root = document) => {
    root.querySelectorAll('[data-femi-bundle-gift]').forEach((panel) => {
      const form = panel.closest('form');
      const selectedPackage = form?.querySelector('[data-femi-package]:checked');
      setGiftPanelState(panel, selectedPackage?.dataset.quantity || 1);
    });
  };

  const getPageGiftRules = () => Array.from(document.querySelectorAll('[data-femi-bundle-gift]'))
    .map((panel) => {
      const form = panel.closest('form');
      return {
        giftVariantId: Number(panel.dataset.giftVariantId),
        minimum: Number(panel.dataset.minQuantity || 2),
        packages: Array.from(form?.querySelectorAll('[data-femi-package]') || [])
          .map((input) => ({
            variantId: Number(input.dataset.variantId),
            units: Number(input.dataset.quantity || 1)
          }))
          .filter((item) => item.variantId && item.units)
      };
    })
    .filter((rule) => rule.giftVariantId && rule.minimum && rule.packages.length);

  const getGiftRules = () => {
    const pageRules = getPageGiftRules();
    if (pageRules.length) {
      try {
        localStorage.setItem(giftRulesStorageKey, JSON.stringify(pageRules));
      } catch (error) {
        // Storage may be unavailable in privacy modes; page rules still work.
      }
      return pageRules;
    }

    try {
      const storedRules = JSON.parse(localStorage.getItem(giftRulesStorageKey) || '[]');
      return Array.isArray(storedRules) ? storedRules : [];
    } catch (error) {
      return [];
    }
  };

  const setButtonLoading = (button, loading) => {
    if (!button) return;
    const spinner = button.querySelector('.loading-overlay__spinner');
    button.classList.toggle('loading', loading);
    button.toggleAttribute('aria-disabled', loading);
    if (spinner) spinner.classList.toggle('hidden', !loading);
  };

  const showFormError = (form, message = '') => {
    const wrapper = form.querySelector('.product-form__error-message-wrapper');
    const target = wrapper?.querySelector('.product-form__error-message');
    if (!wrapper || !target) return;
    target.textContent = message;
    wrapper.hidden = !message;
  };

  const getCart = () => requestJson(cartUrl, { method: 'GET', headers: {} });

  const addBundleToCart = async (form, selectedPackage, giftPanel) => {
    const button = form.querySelector('[name="add"]');
    const giftVariantId = Number(giftPanel.dataset.giftVariantId);
    const mainVariantId = Number(selectedPackage.dataset.variantId || form.querySelector('[name="id"]')?.value);
    const bundleQuantity = Number(selectedPackage.dataset.quantity || 1);
    if (!giftVariantId || !mainVariantId) return;

    setButtonLoading(button, true);
    showFormError(form);

    try {
      const currentCart = await getCart();
      const giftAlreadyInCart = currentCart.items.some((item) =>
        Number(item.variant_id) === giftVariantId && String(item.properties?.[giftProperty]) === 'true'
      );
      const mainItem = {
        id: mainVariantId,
        quantity: 1,
        properties: {
          [requiredGiftProperty]: String(giftVariantId),
          [bundleQuantityProperty]: String(bundleQuantity)
        }
      };
      const items = [];

      if (!giftAlreadyInCart) {
        items.push({
          id: giftVariantId,
          quantity: 1,
          properties: { [giftProperty]: 'true' }
        });
      }
      items.push(mainItem);

      const { renderer, sections } = getSectionsPayload();
      const state = await requestJson(cartAddUrl, {
        method: 'POST',
        body: JSON.stringify({ items, sections, sections_url: window.location.pathname })
      });
      renderCart(state, renderer);
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'femilabs-bundle-gift', productVariantId: mainVariantId });
      }
    } catch (error) {
      showFormError(form, error.message);
    } finally {
      setButtonLoading(button, false);
    }
  };

  const syncCartGift = async () => {
    if (cartSyncInProgress) return;
    cartSyncInProgress = true;
    try {
      const cart = await getCart();
      const qualifyingItems = cart.items.filter((item) => Number(item.properties?.[requiredGiftProperty]) > 0);
      const giftItems = cart.items.filter((item) => String(item.properties?.[giftProperty]) === 'true');
      const requiredGiftIds = new Set(qualifyingItems.map((item) => Number(item.properties[requiredGiftProperty])));
      const giftRules = getGiftRules();

      giftRules.forEach((rule) => {
        const packageUnits = new Map(
          rule.packages.map((item) => [Number(item.variantId), Number(item.units || 1)])
        );
        const totalUnits = cart.items.reduce((sum, item) => {
          if (String(item.properties?.[giftProperty]) === 'true') return sum;
          const units = packageUnits.get(Number(item.variant_id));
          return units ? sum + (Number(item.quantity) * units) : sum;
        }, 0);

        if (totalUnits >= Number(rule.minimum || 2)) {
          requiredGiftIds.add(Number(rule.giftVariantId));
        } else {
          requiredGiftIds.delete(Number(rule.giftVariantId));
        }
      });
      const updates = {};

      giftItems.forEach((item) => {
        const shouldRemain = requiredGiftIds.has(Number(item.variant_id));
        if (!shouldRemain) updates[item.key] = 0;
        else if (item.quantity !== 1) updates[item.key] = 1;
      });

      const missingGiftIds = [...requiredGiftIds].filter((id) =>
        !giftItems.some((item) => Number(item.variant_id) === id)
      );
      const { renderer, sections } = getSectionsPayload();
      let state;

      if (Object.keys(updates).length) {
        state = await requestJson(cartUpdateUrl, {
          method: 'POST',
          body: JSON.stringify({ updates, sections, sections_url: window.location.pathname })
        });
      }

      if (missingGiftIds.length) {
        state = await requestJson(cartAddUrl, {
          method: 'POST',
          body: JSON.stringify({
            items: missingGiftIds.map((id) => ({ id, quantity: 1, properties: { [giftProperty]: 'true' } })),
            sections,
            sections_url: window.location.pathname
          })
        });
      }

      if (state) renderCart(state, renderer);
    } catch (error) {
      console.warn('[Femilabs bundle gift]', error);
    } finally {
      cartSyncInProgress = false;
    }
  };

  document.addEventListener('change', (event) => {
    const input = event.target.closest('[data-femi-package]');
    if (!input) return;
    setGiftPanelState(input.closest('form')?.querySelector('[data-femi-bundle-gift]'), input.dataset.quantity);
  });

  document.addEventListener('submit', (event) => {
    const form = event.target.closest('.femi-product-page form[data-type="add-to-cart-form"]');
    if (!form) return;
    const selectedPackage = form.querySelector('[data-femi-package]:checked');
    const giftPanel = form.querySelector('[data-femi-bundle-gift]');
    if (!selectedPackage || !giftPanel || giftPanel.dataset.unlocked !== 'true') return;
    event.preventDefault();
    event.stopImmediatePropagation();
    addBundleToCart(form, selectedPackage, giftPanel);
  }, true);

  const initialize = () => {
    syncGiftPanels();
    syncCartGift();
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        if (event?.source !== 'femilabs-bundle-gift-sync') syncCartGift();
      });
    }
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initialize);
  else initialize();
  document.addEventListener('shopify:section:load', (event) => syncGiftPanels(event.target));
})();
